<template>
  <transition name="fade">
    <div
      v-if="show"
      ref="overlay"
      class="page-loading-overlay"
      :class="{ 'page-loading-overlay--contained': contained, 'page-loading-overlay--dark': isDarkMode }"
      :style="overlayStyle"
    >
      <div class="loading-content">
        <loading-spinner
          :progress="progress"
          :label="label"
        />
      </div>
    </div>
  </transition>
</template>

<script>
import { mapGetters } from 'vuex';
import LoadingSpinner from '@/components/common/LoadingSpinner.vue';

export default {
  name: 'PageLoadingOverlay',
  components: {
    LoadingSpinner,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: '',
    },
    progress: {
      type: Number,
      default: 0,
    },
    /**
     * When true, the overlay stays within its parent container instead of
     * covering the entire page. Parent must have position: relative.
     * This allows users to interact with elements outside the container (like tabs).
     */
    contained: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      sidebarWidth: 120,
      containedMinHeight: null,
    };
  },
  computed: {
    ...mapGetters('theme', ['isDarkMode']),
    overlayStyle() {
      if (this.contained) {
        return this.containedMinHeight ? { minHeight: `${this.containedMinHeight}px` } : {};
      }
      return {
        left: `${this.sidebarWidth}px`,
      };
    },
  },
  watch: {
    show: {
      handler(newVal) {
        if (this.contained) {
          if (newVal) {
            this.$nextTick(() => {
              this.calculateContainedHeight();
            });
          }
          return;
        }
        if (newVal) {
          this.$nextTick(() => {
            this.moveToBody();
          });
        } else {
          this.removeFromBody();
        }
      },
      immediate: true,
    },
  },
  mounted() {
    if (!this.contained) {
      this.detectSidebarState();
      this.setupSidebarObserver();
      if (this.show) {
        this.$nextTick(() => {
          this.moveToBody();
        });
      }
    } else if (this.show) {
      this.$nextTick(() => {
        this.calculateContainedHeight();
      });
    }
  },
  beforeDestroy() {
    if (this.sidebarObserver) {
      this.sidebarObserver.disconnect();
    }
    if (!this.contained) {
      this.removeFromBody();
    }
  },
  methods: {
    calculateContainedHeight() {
      if (this.$refs.overlay) {
        const rect = this.$refs.overlay.getBoundingClientRect();
        this.containedMinHeight = window.innerHeight - rect.top;
      }
    },
    moveToBody() {
      if (this.$refs.overlay && this.$refs.overlay.parentNode !== document.body) {
        document.body.appendChild(this.$refs.overlay);
      }
    },
    removeFromBody() {
      if (this.$refs.overlay && this.$refs.overlay.parentNode === document.body) {
        document.body.removeChild(this.$refs.overlay);
      }
    },
    detectSidebarState() {
      const sidebar = document.querySelector('.sidebar-fixed');
      if (sidebar) {
        this.sidebarWidth = sidebar.classList.contains('collapsed') ? 12 : 120;
      }
    },
    setupSidebarObserver() {
      const sidebar = document.querySelector('.sidebar-fixed');
      if (sidebar) {
        this.sidebarObserver = new MutationObserver(() => {
          this.sidebarWidth = sidebar.classList.contains('collapsed') ? 12 : 120;
        });
        this.sidebarObserver.observe(sidebar, {
          attributes: true,
          attributeFilter: ['class'],
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/design-system';

.page-loading-overlay {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(1px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1998;
  transition: left 0.3s ease;

  &--contained {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 100;
    border-radius: inherit;
    display: block;

    .loading-content {
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      z-index: 101;
    }
  }

  // Dark mode
  &--dark {
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(4px);

    :deep(.spinner-label) {
      color: $white;
      text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
    }

    :deep(.custom-progress-bar-content) {
      .custom-progress-bar-content__background_parallelogram {
        background: rgba(255, 255, 255, 0.15);
        box-shadow: rem(3) rem(-3) 0 0 rgba(0, 0, 0, 0.8);
      }

      .custom-progress-bar-content__legend__label {
        color: $white;
        text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
      }

      .custom-progress-bar-content__legend__progress_number {
        color: $white;
        text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
      }
    }
  }
}

.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
}

// Fade transition
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
